/**
 * About 5005 — KUK "About the programme".
 *
 * Heading, intro paragraph, and key-content cards that render as a horizontal
 * scroll rail from 768px up and as an accordion below it. Ported from
 * kuk-campus-portal's `components/Landing/AboutCourse.tsx`; see `About5005.tsx`
 * for what v1 hardcodes, what became a field here, and what was dropped.
 *
 * Every field maps to a column that already exists on `about` — the variant
 * adds no schema of its own beyond widening `about.variant`. Several colour
 * columns are borrowed for the nearest matching role (the `key_highlight_*`
 * trio drives the card text and the accordion rows, `highlight_border_color`
 * the card's bottom rule, `read_more_*` the rail's scroll buttons); the labels
 * below name the role, not the column.
 */

export const ABOUT_5005_CONSTANTS = {
  METADATA_TITLES: {
    metadata: "Key content cards",
  },

  DEFAULT_ROW: {
    id: "1",
    title: "",
    description: "",
    priority: 1,
    status: true,
  },

  ABOUT_FIELDS_CONFIG: [
    {
      fieldName: "title",
      label: "Heading",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. About {Online} MBA",
      description:
        "Wrap any phrase in { } to paint it in the highlight colour below — e.g. About {Online} MBA.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "title_text_color",
      label: "Heading colour",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#101112",
      required: false,
      defaultValue: "#101112",
    },
    {
      fieldName: "title_1_color",
      label: "Heading highlight colour",
      value: "#9F1C33",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#9F1C33",
      description: "Applies to the { } run in the heading.",
      required: false,
      defaultValue: "#9F1C33",
    },
    {
      fieldName: "description",
      label: "Intro paragraph",
      value: "",
      valueType: "string",
      filedType: "TEXT_AREA",
      rows: 5,
      placeholder: "Describe the programme",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "description_text_color",
      label: "Intro paragraph colour",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#101112",
      required: false,
      defaultValue: "#101112",
    },
    {
      fieldName: "background_color",
      label: "Section background",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#FFFFFF",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "gradient_color_1",
      label: "Card fill",
      value: "rgba(159, 28, 51, 0.10)",
      valueType: "string",
      filedType: "RGBA",
      placeholder: "rgba(159, 28, 51, 0.10)",
      description:
        "RGBA, not COLOR: the reference tints the card with its brand colour at 10% and that is defined by its opacity.",
      required: false,
      defaultValue: "rgba(159, 28, 51, 0.10)",
    },
    {
      fieldName: "highlight_border_color",
      label: "Card bottom rule / accordion icon colour",
      value: "#9F1C33",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#9F1C33",
      required: false,
      defaultValue: "#9F1C33",
    },
    {
      fieldName: "key_highlight_heading_color",
      label: "Card title colour",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#101112",
      required: false,
      defaultValue: "#101112",
    },
    {
      fieldName: "key_highlight_subtext_color",
      label: "Card description colour",
      value: "#4B4B4B",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#4B4B4B",
      required: false,
      defaultValue: "#4B4B4B",
    },
    {
      fieldName: "key_highlight_divider_color",
      label: "Accordion row background (below 768px)",
      value: "#F5F5F5",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#F5F5F5",
      required: false,
      defaultValue: "#F5F5F5",
    },
    {
      fieldName: "cta_icon",
      label: "Accordion row icon (below 768px)",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload an icon, or leave empty for none",
      description:
        "Decorative — it sits beside the row's own label, so it needs no alt text.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "read_more_background_color",
      label: "Scroll button background",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#FFFFFF",
      description:
        "The rail's ← → buttons. They appear only when there is more to scroll to.",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "read_more_color",
      label: "Scroll button arrow colour",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#101112",
      required: false,
      defaultValue: "#101112",
    },
    {
      fieldName: "status",
      label: "Status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "Enable/Disable section",
      required: false,
      defaultValue: true,
    },
  ],

  METADATA_FIELDS: [
    {
      header: "Card title",
      fieldName: "title",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Leadership and Organisational Management",
      defaultValue: "",
    },
    {
      header: "Card description",
      fieldName: "description",
      value: "",
      valueType: "string",
      filedType: "TEXT_AREA",
      rows: 4,
      placeholder: "What this module covers",
      defaultValue: "",
    },
    {
      header: "Priority",
      fieldName: "priority",
      value: 1,
      valueType: "number",
      filedType: "INPUT",
      defaultValue: 1,
    },
    {
      header: "Status",
      fieldName: "status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
  ],
};
